import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Chip,
    InputAdornment,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    ExpandMore as ExpandMoreIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    PhotoCamera as PhotoCameraIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, VESSEL_HYDRO_TEST_STORE, EMPLOYEE_STORE, TEMPS_STORE } from '../../../config/constants';
import useDictation from '../../../hooks/useDictation';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import CameraCapture from '../../shared/CameraCapture';
import { generateUUID } from '../../../utils/uuid';
import { hasSignatureContent } from '../../../utils/utils';
import Swal from 'sweetalert2';
import vesselHydroTestService from '../../../services/vesselHydroTestService';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

export default function VesselHydroTestForm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [temps, setTemps] = useState([]);

    // Get current user info
    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [form, setForm] = useState({
        uuid: generateUUID(),
        jobCode: '',
        jobName: '',
        customer: '',
        location: '',
        testDate: '',
        designPressure: '',
        testPressure: '',
        duration: '',
        calibratedGaugeNo: '',
        ambientTemperature: '',
        vesselTemperature: '',
        testMediaTemperature: '',
        result: '',
        testerName: userFullName,
        testerId: userId,
        testerType: 'employee',
        remarks: '',
        pictures: [],
        signatureType: 'internal', // 'internal' or 'client'
        internalEmployeeId: userId,
        internalEmployeeName: userFullName,
        internalSignature: '',
        internalSignatureDate: dayjs().format('YYYY-MM-DD'),
        clientName: '',
        clientSignature: '',
        clientSignatureDate: dayjs().format('YYYY-MM-DD'),
        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [signatureEmployeeModalOpen, setSignatureEmployeeModalOpen] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [isDictating, setIsDictating] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);

    const saveTimer = useRef(null);
    const sigPadRef = useRef(null);

    // Dictation for remarks
    const { isSupported: speechSupported, start: startDictation, stop: stopDictation } = useDictation({ lang: 'en-US' });

    // Sort jobs by Job number in descending order
    const sortedJobs = useMemo(() => {
        return [...jobs].sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    // Combine employees and temps for tester selection
    const allPersonnel = useMemo(() => {
        const employeeList = employees.map(emp => ({
            id: emp.id,
            FullName: emp.FullName || emp.name || '',
            name: emp.FullName || emp.name || '',
            type: 'employee'
        }));
        const tempList = temps.map(temp => ({
            id: temp.id,
            FullName: `${temp.FullName || temp.name || ''} (Temp)`,
            name: temp.FullName || temp.name || '',
            type: 'temp'
        }));
        return [...employeeList, ...tempList];
    }, [employees, temps]);

    // Load jobs, employees and temps from IndexedDB
    useEffect(() => {
        (async () => {
            try {
                const db = await initializeDB();

                // Load jobs
                if (db.objectStoreNames.contains(JOB_STORE)) {
                    const tx = db.transaction(JOB_STORE, 'readonly');
                    const store = tx.objectStore(JOB_STORE);
                    const allJobs = await store.getAll();
                    const options = (allJobs || [])
                        .filter(j => j && (j.Job || j.JobName))
                        .map(j => ({ ...j, label: `${j.Job || ''} - ${j.JobName || ''}`.trim() }));
                    setJobs(options);
                }

                // Load employees
                if (db.objectStoreNames.contains(EMPLOYEE_STORE)) {
                    const tx2 = db.transaction(EMPLOYEE_STORE, 'readonly');
                    const store2 = tx2.objectStore(EMPLOYEE_STORE);
                    const allEmployees = await store2.getAll();
                    setEmployees(allEmployees || []);
                }

                // Load temps
                if (db.objectStoreNames.contains(TEMPS_STORE)) {
                    const tx3 = db.transaction(TEMPS_STORE, 'readonly');
                    const store3 = tx3.objectStore(TEMPS_STORE);
                    const allTemps = await store3.getAll();
                    setTemps(allTemps || []);
                }
            } catch (e) {
                console.error('Error loading data:', e);
            }
        })();
    }, []);

    // Load existing draft by uuid if provided
    useEffect(() => {
        const uuid = searchParams.get('uuid');
        if (uuid) {
            loadDraft(uuid);
        }
    }, [searchParams]);

    const loadDraft = async (uuid) => {
        try {
            const draft = await vesselHydroTestService.loadByUuid(uuid);
            if (draft) {
                // If form is already submitted, redirect to list
                if (draft.status === STATUS_SUBMITTED) {
                    navigate('/piping/forms/vessel-hydro-test/list');
                    return;
                }
                setForm(draft);
                setSubmitted(false);
            }
        } catch (e) {
            console.error('Error loading draft:', e);
        }
    };

    // Auto-save function
    const queueSave = (data) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            await saveDraft(data);
        }, 500);
    };

    const saveDraft = async (data) => {
        if (submitted) return;
        try {
            await vesselHydroTestService.saveDraft(data);
            console.log('Draft saved:', data.uuid);
        } catch (e) {
            console.error('Error saving draft:', e);
        }
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    // Handle job selection
    const handleJobChange = (event, newValue) => {
        if (newValue) {
            const updated = {
                ...form,
                jobCode: newValue.Job || '',
                jobName: newValue.JobName || '',
                customer: newValue.Customer || '',
                location: newValue.location || newValue.Location || ''
            };
            setForm(updated);
            queueSave(updated);
        } else {
            const updated = {
                ...form,
                jobCode: '',
                jobName: '',
                customer: '',
                location: ''
            };
            setForm(updated);
            queueSave(updated);
        }
    };

    // Handle tester selection from modal
    const handleSelectTester = (person) => {
        const updated = {
            ...form,
            testerName: person.name || person.FullName || '',
            testerId: person.id,
            testerType: person.type || 'employee'
        };
        setForm(updated);
        queueSave(updated);
        setEmployeeModalOpen(false);
    };

    // Handle internal employee selection for signature
    const handleSelectInternalEmployee = (employee) => {
        const updated = {
            ...form,
            internalEmployeeId: employee.id,
            internalEmployeeName: employee.FullName || employee.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setSignatureEmployeeModalOpen(false);
    };

    // Handle signature section
    const handleSignatureTypeChange = (event) => {
        const newType = event.target.value;
        const updated = {
            ...form,
            signatureType: newType
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleSignatureAccept = () => {
        if (!hasSignatureContent(sigPadRef)) {
            return;
        }

        const dataUrl = sigPadRef.current.toDataURL();
        const dateField = form.signatureType === 'internal' ? 'internalSignatureDate' : 'clientSignatureDate';
        const sigField = form.signatureType === 'internal' ? 'internalSignature' : 'clientSignature';
        const updated = {
            ...form,
            [sigField]: dataUrl,
            [dateField]: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        queueSave(updated);
        setSignatureModalOpen(false);
    };

    const handleClearSignature = () => {
        const sigField = form.signatureType === 'internal' ? 'internalSignature' : 'clientSignature';
        const updated = {
            ...form,
            [sigField]: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    // Handle dictation for remarks
    const handleStartDictation = () => {
        setIsDictating(true);
        startDictation({
            onResult: (transcript) => {
                setForm(currentForm => {
                    const currentText = currentForm.remarks || '';
                    const needsSpace = currentText && !currentText.endsWith(' ');
                    const newText = currentText + (needsSpace ? ' ' : '') + transcript + ' ';
                    const updated = {
                        ...currentForm,
                        remarks: newText
                    };
                    queueSave(updated);
                    return updated;
                });
            }
        });
    };

    const handleStopDictation = () => {
        stopDictation();
        setIsDictating(false);
    };

    // Handle photo capture
    const handlePhotoCapture = (photoData) => {
        const pictures = [...(form.pictures || [])];
        pictures.push({
            id: generateUUID(),
            dataUrl: photoData.dataUrl,
            name: photoData.name || 'photo.jpg',
            timestamp: photoData.timestamp
        });
        const updated = { ...form, pictures };
        setForm(updated);
        queueSave(updated);
        setCameraOpen(false);
    };

    const handleRemovePhoto = (photoId) => {
        const pictures = (form.pictures || []).filter(p => p.id !== photoId);
        const updated = { ...form, pictures };
        setForm(updated);
        queueSave(updated);
    };

    // Form validation
    const validate = () => {
        const newErrors = {};
        if (!form.jobCode) newErrors.jobCode = 'Job is required';
        if (!form.testDate) newErrors.testDate = 'Test Date is required';

        if (form.signatureType === 'internal') {
            if (!form.internalSignature) newErrors.internalSignature = 'Signature is required';
            if (!form.internalEmployeeName) newErrors.internalEmployeeName = 'Employee name is required';
        } else {
            if (!form.clientSignature) newErrors.clientSignature = 'Signature is required';
            if (!form.clientName) newErrors.clientName = 'Client name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submit form
    const handleSubmit = async () => {
        if (!validate()) {
            await Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Please fill in all required fields',
                confirmButtonColor: '#ff9800'
            });
            return;
        }

        setSaving(true);
        try {
            const result = await vesselHydroTestService.submit(form);

            if (result.success) {
                setForm({ ...form, status: STATUS_SUBMITTED, updatedAt: new Date().toISOString() });
                setSubmitted(true);
                setSaving(false);

                const swalResult = await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: result.message || 'Vessel Hydro Test form submitted successfully!',
                    confirmButtonColor: '#4caf50'
                });

                if (swalResult.isConfirmed || swalResult.isDismissed) {
                    navigate('/piping/forms/vessel-hydro-test/list');
                }
            } else {
                throw new Error(result.message || 'Failed to submit form');
            }
        } catch (e) {
            console.error('Error submitting form:', e);
            setSaving(false);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: e.message || 'Failed to submit form. Please try again.',
                confirmButtonColor: '#f44336'
            });
        }
    };

    return (
        <Box sx={{
            minHeight: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper'
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/piping/forms/vessel-hydro-test/list')} color="primary" size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5">Vessel Hydro Test</Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            {form.jobCode ? `Job: ${form.jobCode} - ${form.jobName}` : 'New Test Form'}
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" gap={1}>
                    {form.status === STATUS_DRAFT && (
                        <Chip label="Draft" color="default" size="small" />
                    )}
                    {form.status === STATUS_SUBMITTED && (
                        <Chip label="Submitted" color="success" size="small" />
                    )}
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 1100
                    }}
                >
                    {/* Job Information - Accordion */}
                    <Accordion
                        defaultExpanded
                        elevation={1}
                        sx={{
                            mb: 0.5,
                            mt: 0,
                            '&.Mui-expanded': {
                                margin: 0,
                                mb: 0.5
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '36px !important',
                                height: '36px',
                                py: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Job Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Stack spacing={1.5}>
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Autocomplete
                                                options={sortedJobs}
                                                getOptionLabel={(option) => option.label || ''}
                                                value={sortedJobs.find(j => j.Job === form.jobCode) || null}
                                                onChange={handleJobChange}
                                                disabled={submitted}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        size="small"
                                                        label="Job *"
                                                        error={!!errors.jobCode}
                                                        helperText={errors.jobCode}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Customer"
                                                value={form.customer}
                                                onChange={(e) => handleFieldChange('customer', e.target.value)}
                                                disabled={submitted}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Test Details */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>Test Details</Typography>
                            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Test Date *"
                                        type="date"
                                        value={form.testDate}
                                        onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                        onChange={(e) => handleFieldChange('testDate', e.target.value)}
                                        disabled={submitted}
                                        error={!!errors.testDate}
                                        helperText={errors.testDate}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            style: { cursor: submitted ? 'default' : 'pointer' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Tester Name"
                                        value={form.testerName}
                                        onClick={() => !submitted && setEmployeeModalOpen(true)}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setEmployeeModalOpen(true)} size="small">
                                                        <PersonSearchIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Design Pressure"
                                        value={form.designPressure}
                                        onChange={(e) => handleFieldChange('designPressure', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Test Pressure"
                                        value={form.testPressure}
                                        onChange={(e) => handleFieldChange('testPressure', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Duration"
                                        value={form.duration}
                                        onChange={(e) => handleFieldChange('duration', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Calibrated Gauge #"
                                        value={form.calibratedGaugeNo}
                                        onChange={(e) => handleFieldChange('calibratedGaugeNo', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Ambient Temperature"
                                        value={form.ambientTemperature}
                                        onChange={(e) => handleFieldChange('ambientTemperature', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Vessel Temperature"
                                        value={form.vesselTemperature}
                                        onChange={(e) => handleFieldChange('vesselTemperature', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Test Media Temperature"
                                        value={form.testMediaTemperature}
                                        onChange={(e) => handleFieldChange('testMediaTemperature', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Result"
                                        value={form.result}
                                        onChange={(e) => handleFieldChange('result', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Remarks"
                                        multiline
                                        rows={4}
                                        value={form.remarks}
                                        onChange={(e) => handleFieldChange('remarks', e.target.value)}
                                        disabled={submitted}
                                        InputProps={{
                                            endAdornment: !submitted && speechSupported && (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() =>
                                                            isDictating
                                                                ? handleStopDictation()
                                                                : handleStartDictation()
                                                        }
                                                        color={isDictating ? 'error' : 'default'}
                                                    >
                                                        {isDictating ? <MicOffIcon /> : <MicIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>

                                {/* Pictures Section */}
                                <Grid item xs={12}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Pictures ({form.pictures?.length || 0}/5)
                                        </Typography>
                                        {!submitted && (form.pictures?.length || 0) < 5 && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<PhotoCameraIcon />}
                                                onClick={() => setCameraOpen(true)}
                                            >
                                                Add Photo
                                            </Button>
                                        )}
                                    </Box>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {form.pictures?.map((photo) => (
                                            <Box key={photo.id} position="relative" display="inline-block">
                                                <img
                                                    src={photo.dataUrl}
                                                    alt={photo.name || 'photo'}
                                                    style={{
                                                        width: 80,
                                                        height: 80,
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemovePhoto(photo.id)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -8,
                                                            right: -8,
                                                            backgroundColor: 'error.main',
                                                            color: 'white',
                                                            width: 20,
                                                            height: 20,
                                                            '&:hover': {
                                                                backgroundColor: 'error.dark'
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Signature Section */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1.5} mb={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Signature</Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={form.signatureType}
                                    exclusive
                                    onChange={(e, newValue) => newValue && handleSignatureTypeChange({ target: { value: newValue } })}
                                    disabled={submitted}
                                    size="small"
                                >
                                    <ToggleButton value="internal" sx={{ px: 2 }}>
                                        Internal
                                    </ToggleButton>
                                    <ToggleButton value="client" sx={{ px: 2 }}>
                                        Client/Inspector
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {form.signatureType === 'internal' ? (
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Employee Name *"
                                                value={form.internalEmployeeName}
                                                onClick={() => !submitted && setSignatureEmployeeModalOpen(true)}
                                                InputProps={{
                                                    readOnly: true,
                                                    style: { cursor: submitted ? 'default' : 'pointer' },
                                                    endAdornment: !submitted && (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setSignatureEmployeeModalOpen(true)} size="small">
                                                                <PersonSearchIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                error={!!errors.internalEmployeeName}
                                                helperText={errors.internalEmployeeName}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date"
                                                type="date"
                                                value={form.internalSignatureDate}
                                                onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                onChange={(e) => handleFieldChange('internalSignatureDate', e.target.value)}
                                                disabled={submitted}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    style: { cursor: submitted ? 'default' : 'pointer' }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" justifyContent="center" alignItems="center">
                                                {form.internalSignature ? (
                                                    <Box position="relative" display="inline-block">
                                                        <img
                                                            src={form.internalSignature}
                                                            alt="Signature"
                                                            style={{
                                                                maxWidth: 200,
                                                                height: 60,
                                                                border: '1px solid #ddd',
                                                                borderRadius: 4,
                                                                cursor: submitted ? 'default' : 'pointer'
                                                            }}
                                                            onClick={() => !submitted && setSignatureModalOpen(true)}
                                                            title={submitted ? '' : 'Click to edit signature'}
                                                        />
                                                        {!submitted && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleClearSignature}
                                                                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                            >
                                                                <ClearIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DrawIcon />}
                                                        onClick={() => setSignatureModalOpen(true)}
                                                        disabled={submitted}
                                                    >
                                                        Add Signature
                                                    </Button>
                                                )}
                                            </Box>
                                            {errors.internalSignature && (
                                                <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                    {errors.internalSignature}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Client/Inspector Name *"
                                                value={form.clientName}
                                                onChange={(e) => handleFieldChange('clientName', e.target.value)}
                                                disabled={submitted}
                                                error={!!errors.clientName}
                                                helperText={errors.clientName}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date"
                                                type="date"
                                                value={form.clientSignatureDate}
                                                onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                onChange={(e) => handleFieldChange('clientSignatureDate', e.target.value)}
                                                disabled={submitted}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    style: { cursor: submitted ? 'default' : 'pointer' }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" justifyContent="center" alignItems="center">
                                                {form.clientSignature ? (
                                                    <Box position="relative" display="inline-block">
                                                        <img
                                                            src={form.clientSignature}
                                                            alt="Signature"
                                                            style={{
                                                                maxWidth: 200,
                                                                height: 60,
                                                                border: '1px solid #ddd',
                                                                borderRadius: 4,
                                                                cursor: submitted ? 'default' : 'pointer'
                                                            }}
                                                            onClick={() => !submitted && setSignatureModalOpen(true)}
                                                            title={submitted ? '' : 'Click to edit signature'}
                                                        />
                                                        {!submitted && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleClearSignature}
                                                                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                            >
                                                                <ClearIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DrawIcon />}
                                                        onClick={() => setSignatureModalOpen(true)}
                                                        disabled={submitted}
                                                    >
                                                        Add Signature
                                                    </Button>
                                                )}
                                            </Box>
                                            {errors.clientSignature && (
                                                <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                    {errors.clientSignature}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            {/* Modal for Tester Name selection (employees + temps) */}
            <SelectSingleEmployeeModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleSelectTester}
                employees={allPersonnel}
            />

            {/* Modal for Internal Signature Employee selection (only employees) */}
            <SelectSingleEmployeeModal
                open={signatureEmployeeModalOpen}
                onClose={() => setSignatureEmployeeModalOpen(false)}
                onSelect={handleSelectInternalEmployee}
                employees={employees}
            />

            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.signatureType === 'internal' ? form.internalEmployeeName : form.clientName}
                onAccept={handleSignatureAccept}
                existingSignature={form.signatureType === 'internal' ? form.internalSignature : form.clientSignature}
            />

            <CameraCapture
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={handlePhotoCapture}
            />

            <Backdrop open={saving} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
}
